import { Component, computed, effect, inject, signal, ViewChild } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AuthService } from '@lib/auth/auth.service';
import { OuiLayoutModule } from 'omnium-ui/layout';
import { MenuNode } from 'omnium-ui/shared';
import { GestionnaireService } from './_shared/services/gestionnaire/gestionnaire.service';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiLoadingModule } from 'omnium-ui/loading';
import { GlobalErrorPageComponent } from '../../../front-gestion-reseau-common/src/lib/components/global-error-page/global-error-page.component';
import { COMMON_ENVIRONMENT } from '@lib/core/environment/common-environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, OuiLayoutModule, OuiLoadingModule, GlobalErrorPageComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private readonly authService = inject(AuthService);
  private readonly gestionnaireService = inject(GestionnaireService);
  private readonly snackbarService = inject(OuiSnackbarService);
  private readonly commonEnvironment = inject(COMMON_ENVIRONMENT);

  @ViewChild(GlobalErrorPageComponent)
  globalErrorPageComponent?: GlobalErrorPageComponent;

  readonly isLoading = signal(false);
  readonly isLoggedIn = computed<boolean>(() => !!this.authService.userInfo());
  readonly userType = computed<string>(() => this.authService.userInfo()?.userDir ?? '');

  private baseMenu: MenuNode[] = [
    {
      id: 'consultants',
      name: 'Consultants',
      routeLink: '/consultants',
    },
    {
      id: 'habilitation',
      name: 'Habilitations',
      routeLink: '/habilitations',
    },
    {
      id: 'validation-pj',
      name: 'Dépôt de pièces',
      routeLink: '/depot-de-pieces',
    },
    {
      id: 'affiliation',
      name: 'Affiliations',
      routeLink: '/affiliations',
    },
    {
      id: 'renouvellement',
      name: 'Renouvellements',
      routeLink: '/renouvellements',
    },
    {
      id: 'resiliation',
      name: 'Résiliations',
      routeLink: '/resiliations',
    },
  ];

  readonly menu = computed<MenuNode[]>(() => {
    return this.authService.isOnlyGcom()
      ? this.baseMenu.filter(
          item =>
            item.id !== 'affiliation' &&
            item.id !== 'renouvellement' &&
            item.id !== 'resiliation'
        )
      : this.baseMenu;
  });

  readonly userName = this.authService.userName;

  constructor() {
    const user = this.authService.login();

    if (this.authService.isOnlyGcom()) {
      const hab = this.baseMenu.find(bm => bm.id === 'validation-pj');
      hab!.routeLink = '/depot-de-pieces-gcom';
    }

    setTimeout(() => {
      if (!this.authService.userInfo() && user) {
        this.isLoading.set(true);
      }
      if (!this.isLoading() && !user) {
        this.globalErrorPageComponent?.open();
      }
    }, 100);

    effect(() => {
      if (this.authService.userInfo()) {
        this.initGestionnaires();
      }
    });
  }

  private async initGestionnaires() {
    try {
      await this.gestionnaireService.createGestionnaireIfNotExist();
    } catch (error) {
      console.error(error);
      this.snackbarService.open(
        "Une erreur est survenue durant l'enregistrement de l'utilisateur",
        'error'
      );
      return;
    }

    try {
      await this.gestionnaireService.fetchGestionnaires();
    } catch (error) {
      console.error(error);
      this.snackbarService.open(
        'Une erreur est survenue durant la récupération de la liste des gestionnaires',
        'error'
      );
      return;
    }

    this.isLoading.set(false);
  }

  userMenu: MenuNode[] = [
    {
      name: 'Se déconnecter',
      action: () => {
        this.authService.logout();
      },
    },
  ];
}
